import { DynamicPageVariant, Website } from '@betterfunnel/types';
import {
  ArrowTopRightOnSquareIcon,
  ArrowTurnDownRightIcon,
  BeakerIcon,
  PencilIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { ChevronDownIcon, PlusIcon } from '@heroicons/react/24/solid';
import * as Sentry from '@sentry/react';
import { ColumnDef } from '@tanstack/react-table';
import { QueryDocumentSnapshot } from 'firebase/firestore';
import Lottie from 'lottie-react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import websiteBuildingAnimation from '../../assets/lotties/website-building.json';
import DynamicStats from '../../components/analytics/DynamicStats';
import { StatCellValues } from '../../components/analytics/StatCellValues';
import { StatsGridItem } from '../../components/analytics/StatsGrid';
import {
  AnalyticsTypeButton,
  AnalyticsTypeOption,
} from '../../components/AnalyticsTypeButton';
import AlertModal from '../../components/Common/AlertModal';
import Badge from '../../components/Common/Badge';
import Button from '../../components/Common/Button';
import IconButton from '../../components/Common/IconButton';
import Loader from '../../components/Common/Loader';
import MenuButton from '../../components/Common/MenuButton';
import Tooltip from '../../components/Common/Tooltip';
import { CustomDataGrid } from '../../components/CustomDataGrid/CustomDataGrid';
import DashboardAnalyticsRangeSelector from '../../components/DashboardAnalyticsRangeSelector';
import NoVariantExists from '../../components/DynamicPages/NoVariantExists';
import { DashboardContext } from '../../contexts/DashboardContext';
import { DynamicPageContext } from '../../contexts/DynamicPageContext';
import { SnackbarContext } from '../../contexts/SnackbarContext';
import { UserContext } from '../../contexts/UserContext';
import { useHasFeature } from '../../hooks/useHasFeature';
import CopyDynamicPageLinkModal from '../../modals/CopyDynamicPageLinkModal';
import CreateVariantModal, {
  CreateVariantData,
} from '../../modals/CreateVariantModal';
import EditorModal from '../../modals/EditorModal';
import EditTestDescription from '../../modals/EditTestDescription';
import EditTrafficSplitModal from '../../modals/EditTrafficSplitModal';
import EditVariantConstraintsModal from '../../modals/EditVariantConstraintsModal';
import EditVariantNameAndDescription from '../../modals/EditVariantNameAndDescription';
import WebsitePreviewModal from '../../modals/WebsitePreviewModal';
import { getExternalAnalyticsByDates } from '../../services/AnalyticsService';
import {
  duplicateVariant,
  duplicateVariantAsNewDynamicPage,
  duplicateWebsiteAsVariant,
  duplicateWebsiteInVariant,
  endExperiment,
  removeWebsiteFromVariant,
  startExperiment,
  updateVariant,
} from '../../services/DynamicPageVariantsService';
import { analyticsRangeToDates, classNames } from '../../utils';
import { getWeightInPercentage } from '../../utils/variant-utils';

const testsChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

type DynamicPageVariantsRowData =
  | {
      type: 'test';
      test: QueryDocumentSnapshot<Website>;
      variant: QueryDocumentSnapshot<DynamicPageVariant>;
      tests: QueryDocumentSnapshot<Website>[];
      testIndex: number;
    }
  | {
      type: 'variant';
      variant: QueryDocumentSnapshot<DynamicPageVariant>;
      tests: QueryDocumentSnapshot<Website>[];
    };

const getRowStatus = (row: DynamicPageVariantsRowData) => {
  if (row.type === 'test') {
    return row.test.data().status === 'creating' ? 'creating' : 'active';
  } else {
    return row.tests.length &&
      row.tests.every((t) => t.data().status === 'creating')
      ? 'creating'
      : row.variant.data().status;
  }
};

export default function ManageDynamicPageDetails() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const { show, hide } = useContext(SnackbarContext);
  const hasFinancialDataFeature = useHasFeature('viewFinancialData');
  const {
    selectedAccount,
    analyticsCustomEvents,
    analyticsExternalPixelEvents,
  } = useContext(UserContext);
  const {
    selectedDynamicPage,
    analyticsRange,
    analyticsType,
    setAnalyticsType,
  } = useContext(DashboardContext);

  const { variants, websites, customRangeAnalytics, primaryDomain } =
    useContext(DynamicPageContext);

  const [adsAccountAnalytics, setAdsAccountAnalytics] = useState<Record<
    string,
    any
  > | null>();
  const [, setIsLoadingAdsAccountAnalytics] = useState(false);

  const [createVariantModalOpen, setCreateVariantModalOpen] =
    useState<CreateVariantData>();
  const [copyLinkModalOpen, setCopyLinkModalOpen] = useState(false);
  const [openEditor, setOpenEditor] = useState<string>();
  const [editVariantConstraintsModelOpen, setEditVariantConstraintsModelOpen] =
    useState<{ variantId: string }>();
  const [editNameAndDescriptionModalOpen, setEditNameAndDescriptionModalOpen] =
    useState<{ variantId: string }>();
  const [editTestDescriptionModalOpen, setEditTestDescriptionModalOpen] =
    useState<{ testId: string }>();
  const [editTrafficSplitModal, setEditTrafficSplitModal] = useState<{
    variantId: string;
    tests: QueryDocumentSnapshot<Website>[];
  }>();
  const [analyticsModalOpen, setAnalyticsModalOpen] = useState<{
    websiteId: string;
  }>();

  const [deleteConfirmationModalOpen, setDeleteConfirmationModalOpen] =
    useState<{
      entity: string;
      name: string;
      onConfirm: () => void | Promise<void>;
    }>();

  const [selectedGoogleConversionAction, setSelectedGoogleConversionAction] =
    useState<string | null>(null);

  const stats = useMemo((): StatsGridItem[] => {
    if (openEditor) {
      return [];
    }

    const analyticsData = customRangeAnalytics;

    const analyticsKey = 'custom';

    return [
      {
        id: 'conversion-rate',
        name: 'Conversion Rate',
        value: (
          <StatCellValues
            analyticsType={analyticsType}
            analyticsKey={analyticsKey}
            analyticsData={analyticsData}
            format={(attrs) => (attrs.visitors ? `${attrs.cr}%` : '-')}
            showAll
          />
        ),
        // change: '+100%',
        // changeType: 'positive',
      },
      {
        id: 'visitors',
        name: 'Visitors',
        value: (
          <StatCellValues
            analyticsType={analyticsType}
            analyticsKey={analyticsKey}
            analyticsData={analyticsData}
            format={(attrs) => attrs.visitors ?? '-'}
            showAll
          />
        ),
        // change: '+100%',
        // changeType: 'positive',
      },
      {
        id: 'conversions',
        name: 'Conversions',
        value: (
          <StatCellValues
            analyticsType={analyticsType}
            analyticsKey={analyticsKey}
            analyticsData={analyticsData}
            format={(attrs) => attrs.conversions ?? '-'}
            showAll
          />
        ),
        // change: '+100%',
        // changeType: 'positive',
      },

      {
        id: 'active-variants',
        name: 'Active Variants',
        value: websites?.filter((w) => w.data().status === 'active')?.length
          ? websites?.filter((w) => w.data().status === 'active')?.length
          : '-',
      },
    ];
  }, [openEditor, customRangeAnalytics, analyticsType, websites]);

  useEffect(() => {
    if (!selectedAccount || !selectedDynamicPage || !analyticsRange) return;
    if (!Object.keys(selectedDynamicPage.data().connections ?? {}).length) {
      setAdsAccountAnalytics(null);
      return;
    }

    const { startDate, endDate } = analyticsRangeToDates(analyticsRange);

    setIsLoadingAdsAccountAnalytics(true);
    setAdsAccountAnalytics(undefined);
    getExternalAnalyticsByDates(
      selectedAccount.id,
      selectedDynamicPage.id,
      startDate,
      endDate,
    )
      .then((res) => {
        setAdsAccountAnalytics(res || null);
      })
      .finally(() => {
        setIsLoadingAdsAccountAnalytics(false);
      });
  }, [analyticsRange, selectedDynamicPage, selectedAccount]);

  const adAccountStats = useMemo((): StatsGridItem[] => {
    if (openEditor) {
      return [];
    }
    const currencyFormatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: adsAccountAnalytics?.currencyCode || 'USD',
    });
    const numberFormatter = new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 1,
    });
    const percentageFormatter = new Intl.NumberFormat('en-US', {
      style: 'percent',
    });

    const loading = (
      <div className="h-2.5 w-1/2 min-w-7 max-w-12 animate-pulse rounded-md bg-gray-300"></div>
    );

    const selectedGoogleConversion = selectedGoogleConversionAction
      ? adsAccountAnalytics?.conversionsPerActionArray?.find(
          (c: any) => c.actionName === selectedGoogleConversionAction,
        )
      : null;

    return [
      ...(hasFinancialDataFeature
        ? [
            {
              id: 'cpc',
              name: 'CPC (avg)',
              value:
                adsAccountAnalytics !== undefined
                  ? adsAccountAnalytics?.['cpc']
                    ? currencyFormatter.format(
                        Math.ceil((adsAccountAnalytics?.['cpc'] ?? 0) * 100) /
                          100,
                      )
                    : '-'
                  : loading,

              // change: '-10%',
              // changeType: 'positive',
            },
          ]
        : []),
      {
        id: 'ad-clicks',
        name: 'Ad Clicks',
        value:
          adsAccountAnalytics !== undefined
            ? adsAccountAnalytics?.['clicks']
              ? numberFormatter.format(adsAccountAnalytics?.['clicks'])
              : '-'
            : loading,
        // change: '+100%',
        // changeType: 'positive',
      },
      {
        id: 'google-conversions',
        name: (
          <span>
            <MenuButton
              disabled={!adsAccountAnalytics?.conversionsPerActionArray?.length}
              customButton={
                <div className="flex w-full flex-row items-start justify-between gap-2">
                  <span className="truncate sm:max-w-36">
                    {selectedGoogleConversionAction || 'Google Conversions'}
                  </span>
                  {(!!adsAccountAnalytics?.conversionsPerActionArray?.length ||
                    selectedGoogleConversionAction) && (
                    <ChevronDownIcon className="mt-1 size-4" />
                  )}
                </div>
              }
              items={[
                {
                  title: 'All',
                  onClick: () => {
                    setSelectedGoogleConversionAction(null);
                  },
                },
                ...(adsAccountAnalytics?.conversionsPerActionArray?.map(
                  (c: any) => ({
                    title: c.actionName,
                    onClick: () => {
                      setSelectedGoogleConversionAction(c.actionName);
                    },
                  }),
                ) || []),
              ]}
            />
          </span>
        ),
        value:
          adsAccountAnalytics !== undefined
            ? selectedGoogleConversionAction
              ? selectedGoogleConversion
                ? numberFormatter.format(selectedGoogleConversion.conversions)
                : '-'
              : adsAccountAnalytics?.['conversions']
                ? numberFormatter.format(adsAccountAnalytics?.['conversions'])
                : '-'
            : loading,
        // change: '+100%',
        change: '',
        // secondaryValue:
        //   adsAccountAnalytics?.['clicks'] &&
        //   adsAccountAnalytics?.['conversions']
        //     ? `${Math.ceil(
        //         ((adsAccountAnalytics?.['conversions'] ?? 0) /
        //           (adsAccountAnalytics?.['clicks'] ?? 0)) *
        //           100,
        //       )}%`
        //     : '-',
        // changeType: 'positive',
      },
      {
        id: 'avgSpeedScore',
        name: 'Page Speed Score',
        value: adsAccountAnalytics?.['avgSpeedScore']
          ? percentageFormatter.format(
              Math.ceil((adsAccountAnalytics?.['avgSpeedScore'] ?? 0) * 100),
            )
          : '-',
        // changeType: 'positive',
      },
    ];
  }, [
    adsAccountAnalytics,
    hasFinancialDataFeature,
    openEditor,
    selectedGoogleConversionAction,
  ]);

  const onDeleteVariantButtonClick = useCallback(
    (variant: QueryDocumentSnapshot<DynamicPageVariant>) => {
      setDeleteConfirmationModalOpen({
        entity: 'variant',
        name: variant.data().name,
        onConfirm: async () => {
          if (!selectedAccount || !selectedDynamicPage) return;
          updateVariant(
            selectedAccount.id,
            selectedDynamicPage.id,
            variant.id,
            { status: 'deleted' },
          );
        },
      });
    },
    [selectedAccount, selectedDynamicPage],
  );

  const onDeleteTestButtonClick = useCallback(
    (
      test: QueryDocumentSnapshot<Website>,
      variant: QueryDocumentSnapshot<DynamicPageVariant>,
    ) => {
      setDeleteConfirmationModalOpen({
        entity: 'test',
        name: test.data().name || 'Test',
        onConfirm: async () => {
          removeWebsiteFromVariant(
            selectedAccount?.id ?? '',
            selectedDynamicPage?.id ?? '',
            variant.id,
            test.id,
          );
        },
      });
    },
    [selectedAccount?.id, selectedDynamicPage?.id],
  );

  const getItemMetadataCached = useMemo(() => {
    const _cache = new Map<string, ReturnType<typeof _getMetadata>>();

    const _getMetadata = (row: DynamicPageVariantsRowData) => {
      const analyticsData = customRangeAnalytics;

      const status = getRowStatus(row);

      const websiteAnalytics =
        status === 'creating'
          ? undefined
          : analyticsData
            ? row.type === 'test'
              ? (analyticsData?.test?.[row.test.id] ?? {})
              : (analyticsData?.variant?.[row.variant.id] ?? {})
            : undefined;

      const analyticsKey = 'custom';

      return {
        websiteAnalytics,
        analyticsKey,
        analyticsData,
      };
    };

    return (row: DynamicPageVariantsRowData) => {
      const key =
        row.type + '-' + (row.type === 'test' ? row.test.id : row.variant.id);
      const existing = _cache.get(key);
      if (!existing) {
        const md = _getMetadata(row);
        _cache.set(key, md);
        return md;
      }

      return existing;
    };
  }, [customRangeAnalytics]);

  const rows: DynamicPageVariantsRowData[] = useMemo(() => {
    return (
      variants
        ?.map((variant) => {
          const tests =
            websites?.filter((w) => variant.data().websiteIds.includes(w.id)) ??
            [];

          return [
            { variant, type: 'variant', tests },
            ...(tests.length > 1
              ? tests.map((test, i) => ({
                  type: 'test' as const,
                  variant,
                  test,
                  testIndex: i,
                  tests,
                }))
              : []),
          ] satisfies DynamicPageVariantsRowData[];
        })
        .flat() ?? []
    );
  }, [variants, websites]);

  const columns: ColumnDef<DynamicPageVariantsRowData>[] = useMemo(() => {
    if (openEditor) {
      return [];
    }
    return [
      {
        id: 'name',
        header: 'Name',
        accessorFn: (row) =>
          row.type === 'variant'
            ? row.variant.data().name
            : row.test.data().name,
        size: 240,
        cell: ({ row: { original } }) => {
          const percentageFormatter = new Intl.NumberFormat('en-US', {
            style: 'percent',
            maximumFractionDigits: 2,
          });

          const weightInPercentage =
            original.type === 'test'
              ? getWeightInPercentage(original.variant.data(), original.test.id)
              : 0;

          return (
            <>
              {original.type === 'variant' && (
                <div className="flex-col">
                  <div
                    className="group relative flex cursor-pointer items-center gap-1"
                    onClick={() => {
                      setEditNameAndDescriptionModalOpen({
                        variantId: original.variant.id,
                      });
                    }}
                  >
                    <div className="font-medium text-gray-900">
                      {original.variant.data().name}
                    </div>
                    <div className="absolute -end-4 hidden group-hover:block">
                      <PencilIcon className="h-3 w-3 text-gray-400 group-hover:text-gray-500" />
                    </div>
                  </div>
                  {original.variant.data().description && (
                    <Tooltip
                      text={original.variant.data().description}
                      placement="right"
                    >
                      <div
                        className="max-w-44 overflow-hidden text-ellipsis whitespace-nowrap font-medium text-gray-500"
                        onClick={() => {
                          setEditNameAndDescriptionModalOpen({
                            variantId: original.variant.id,
                          });
                        }}
                      >
                        {original.variant.data().description}
                      </div>
                    </Tooltip>
                  )}
                </div>
              )}
              {original.type === 'test' && (
                <div className="flex w-full max-w-72 flex-col justify-between gap-1.5">
                  <div className="flex w-full flex-row gap-3">
                    <div className="mt-0.5 h-4">
                      <ArrowTurnDownRightIcon className="h-4 w-4 text-gray-400" />
                    </div>
                    <div className="ms-0 flex min-w-24 flex-col items-start justify-start gap-2">
                      <span
                        className={classNames(
                          'inline-flex items-center rounded-md bg-blue-50 px-2 py-1.5 text-xs font-medium capitalize text-blue-700 ring-1 ring-inset ring-green-600/20',
                          original.testIndex !== 0
                            ? 'bg-orange-50 text-orange-700'
                            : '',
                        )}
                      >
                        {original.testIndex === 0
                          ? 'Original'
                          : original.tests.length === 2
                            ? 'Experiment'
                            : `Experiment ${testsChars[original.testIndex - 1] ?? testsChars[testsChars.length - 1]}`}
                      </span>
                    </div>

                    <div
                      className="group relative flex cursor-pointer items-center"
                      onClick={() => {
                        setEditTrafficSplitModal({
                          variantId: original.variant.id,
                          tests: original.tests,
                        });
                      }}
                    >
                      <span>
                        {percentageFormatter.format(weightInPercentage / 100)}
                      </span>
                      <div className="absolute -end-4 hidden group-hover:block">
                        <PencilIcon className="h-3 w-3 text-gray-400 group-hover:text-gray-500" />
                      </div>
                    </div>
                  </div>
                  <div className="flex w-full flex-col gap-1 ps-8">
                    <Tooltip
                      text={original.test.data()?.name || ''}
                      placement="right"
                    >
                      <div
                        className="max-w-72 overflow-hidden text-ellipsis whitespace-nowrap font-medium text-gray-500"
                        onClick={() => {
                          setEditTestDescriptionModalOpen({
                            testId: original.test.id,
                          });
                        }}
                      >
                        {original.test.data().name}
                      </div>
                    </Tooltip>
                  </div>
                </div>
              )}
            </>
          );
        },
      },
      {
        id: 'status',
        header: 'Status',
        accessorFn: (row) => {
          return getRowStatus(row);
        },
        size: 140,
        cell: ({ row: { original } }) => {
          const status = getRowStatus(original);
          if (original.type === 'test') return <></>;

          return (
            <Badge
              title={
                status === 'active'
                  ? 'Active'
                  : status === 'creating'
                    ? 'Generating'
                    : 'Inactive'
              }
              rounded={false}
              hideDot
              showBorder
              color={
                status === 'active'
                  ? 'green'
                  : status === 'creating'
                    ? 'blue'
                    : 'gray'
              }
              size="normal"
            />
          );
        },
      },
      {
        id: 'itemId',
        header: 'ID',
        accessorFn: (row) => {
          return row.type === 'variant' ? row.variant.id : row.test.id;
        },
        size: 200,
      },
      {
        id: 'constraints',
        header: 'Smart Traffic',
        accessorKey: 'constraints',
        size: 210,
        cell: ({ row: { original } }) => {
          if (original.type === 'test') return <></>;
          const { constraints } = original.variant.data();

          const hasConstraints = constraints
            ? Object.values(constraints).some((c) => c.length)
            : false;

          return (
            <Button
              variant="secondary"
              size="small"
              icon={<SparklesIcon className="h-4 w-4" />}
              className={hasConstraints ? 'bg-blue-50 text-blue-700' : ''}
              onClick={() => {
                setEditVariantConstraintsModelOpen({
                  variantId: original.variant.id,
                });
              }}
            >
              {hasConstraints ? 'Edit Rules' : 'Set Rules'}
            </Button>
          );
        },
      },

      {
        id: 'visitors',
        header: 'Visitors',
        accessorKey: 'analyticsData.visitors',
        size: 120,
        cell: ({ row: { original } }) => {
          const { analyticsKey, websiteAnalytics } =
            getItemMetadataCached(original);
          return (
            <StatCellValues
              analyticsType={analyticsType}
              analyticsKey={analyticsKey}
              analyticsData={websiteAnalytics}
              format={(attrs) => attrs.visitors ?? '-'}
              showAll
              fill
            />
          );
        },
      },
      {
        id: 'conversions',
        header: 'Conversions',
        accessorKey: 'analyticsData.conversions',
        size: 120,
        cell: ({ row: { original } }) => {
          const { websiteAnalytics, analyticsKey } =
            getItemMetadataCached(original);
          return (
            <StatCellValues
              analyticsType={analyticsType}
              analyticsKey={analyticsKey}
              analyticsData={websiteAnalytics}
              format={(attrs) => attrs.conversions ?? '-'}
              showAll
              fill
            />
          );
        },
      },
      {
        id: 'cr',
        header: 'Conversion rate',
        accessorKey: 'campaignExtra.cr',
        size: 170,
        cell: ({ row: { original } }) => {
          const { websiteAnalytics, analyticsKey } =
            getItemMetadataCached(original);
          return (
            <StatCellValues
              analyticsType={analyticsType}
              analyticsKey={analyticsKey}
              analyticsData={websiteAnalytics}
              format={(attrs) => (attrs.visitors ? `${attrs.cr}%` : '-')}
              showAll
              fill
            />
          );
        },
      },
      {
        id: 'buttonClicks',
        header: 'Button Clicks',
        accessorKey: 'analyticsData.buttonClicks',
        size: 170,
        cell: ({ row: { original } }) => {
          const { websiteAnalytics, analyticsKey } =
            getItemMetadataCached(original);
          return (
            <StatCellValues
              analyticsType={analyticsType}
              analyticsKey={analyticsKey}
              analyticsData={websiteAnalytics}
              format={(attrs) => attrs.buttonClicks ?? '-'}
              showAll
              fill
            />
          );
        },
      },
      {
        id: 'uniqueLinkClicks',
        header: 'Unique link clicks',
        accessorKey: 'analyticsData.uniqueButtonClicks',
        size: 170,
        cell: ({ row: { original } }) => {
          const { analyticsKey, websiteAnalytics } =
            getItemMetadataCached(original);
          return (
            <StatCellValues
              analyticsType={analyticsType}
              analyticsKey={analyticsKey}
              analyticsData={websiteAnalytics}
              format={(attrs) => {
                const val = attrs.uniqueButtonClicks;
                const percentageFormatter = new Intl.NumberFormat('en-US', {
                  style: 'percent',
                  maximumFractionDigits: 0,
                });
                return (
                  <>
                    <span>{val ?? '-'}</span>
                    <span className="ms-1 text-xs opacity-50">
                      (
                      {percentageFormatter.format(
                        (val ?? 0) / (attrs.visitors || 1),
                      )}
                      )
                    </span>
                  </>
                );
              }}
              showAll={false}
              fill
            />
          );
        },
      },
      {
        id: 'formSubmissions',
        header: 'Form Submissions',
        accessorKey: 'analyticsData.formSubmissions',
        size: 170,
        cell: ({ row: { original } }) => {
          const { websiteAnalytics, analyticsKey } =
            getItemMetadataCached(original);
          return (
            <StatCellValues
              analyticsType={analyticsType}
              analyticsKey={analyticsKey}
              analyticsData={websiteAnalytics}
              format={(attrs) => attrs.formSubmissions ?? '-'}
              showAll
              fill
            />
          );
        },
      },

      ...(analyticsCustomEvents?.map(
        (event) =>
          ({
            header: `Event: ${event.name}`,
            accessorKey: `analyticsData.customEvent.${event.id}`,
            size: 250,
            cell: ({ row: { original } }) => {
              const { analyticsKey, analyticsData, websiteAnalytics } =
                getItemMetadataCached(original);
              return (
                <StatCellValues
                  analyticsType={analyticsType}
                  analyticsKey={analyticsKey}
                  analyticsData={websiteAnalytics}
                  analyticsCustomEventsData={
                    analyticsData
                      ? (analyticsData?.customEvents?.[original.type]?.[
                          original.type === 'variant'
                            ? original.variant.id
                            : original.test.id
                        ] ?? {})
                      : undefined
                  }
                  analyticsCustomEvents={analyticsCustomEvents}
                  format={(attrs) => {
                    const percentageFormatter = new Intl.NumberFormat('en-US', {
                      style: 'percent',
                      maximumFractionDigits: 0,
                    });
                    return (
                      <>
                        <span>{attrs[event.id] ?? '-'}</span>
                        <span className="ms-1 text-xs opacity-50">
                          (
                          {percentageFormatter.format(
                            (attrs[event.id] ?? 0) / (attrs.visitors || 1),
                          )}
                          )
                        </span>
                      </>
                    );
                  }}
                  showAll={false}
                  fill
                />
              );
            },
          }) satisfies ColumnDef<DynamicPageVariantsRowData>,
      ) ?? []),

      ...(analyticsExternalPixelEvents?.map(
        (event) =>
          ({
            header: `Event: ${event.name}`,
            accessorKey: `analyticsData.externalPixels.${event.pixelType}`,
            size: 250,
            cell: ({ row: { original } }) => {
              const { analyticsKey, analyticsData, websiteAnalytics } =
                getItemMetadataCached(original);
              return (
                <StatCellValues
                  analyticsType={analyticsType}
                  analyticsKey={analyticsKey}
                  analyticsData={websiteAnalytics}
                  analyticsCustomEventsData={
                    analyticsData
                      ? (analyticsData?.externalPixels?.[event.pixelType]?.[
                          original.type
                        ]?.[
                          original.type === 'variant'
                            ? original.variant.id
                            : original.test.id
                        ] ?? {})
                      : undefined
                  }
                  analyticsCustomEvents={analyticsExternalPixelEvents}
                  format={(attrs) => {
                    const percentageFormatter = new Intl.NumberFormat('en-US', {
                      style: 'percent',
                      maximumFractionDigits: 0,
                    });
                    return (
                      <>
                        <span>{attrs[event.id] ?? '-'}</span>
                        <span className="ms-1 text-xs opacity-50">
                          (
                          {percentageFormatter.format(
                            (attrs[event.id] ?? 0) / (attrs.visitors || 1),
                          )}
                          )
                        </span>
                      </>
                    );
                  }}
                  showAll={false}
                  fill
                />
              );
            },
          }) satisfies ColumnDef<DynamicPageVariantsRowData>,
      ) ?? []),

      {
        header: '',
        id: 'experiment',
        size: 200,
        cell: ({ row: { original } }) => {
          return original.type === 'variant' ? (
            original.tests.length > 1 ? (
              <></>
            ) : (
              <Button
                variant="secondary"
                size="small"
                icon={<BeakerIcon className="h-4 w-4" />}
                onClick={(e) => {
                  e.stopPropagation();

                  if (!selectedAccount || !selectedDynamicPage) return;

                  const sb = show('Starting experiment...', 'info', {
                    disableAutoClose: true,
                  });
                  startExperiment(
                    selectedAccount.id,
                    selectedDynamicPage.id,
                    original.variant.id,
                  )
                    .then(() => {
                      show('Experiment started', 'info');
                    })
                    .catch((e) => {
                      console.error(e);
                      show('Failed to start experiment', 'warn');
                      Sentry.captureException(e);
                    })
                    .finally(() => {
                      hide(sb.id);
                    });
                }}
              >
                Start Experiment
              </Button>
            )
          ) : (
            <></>
          );
        },
      },
      {
        header: '',
        id: 'actions',
        size: 180,
        cell: ({ row: { original } }) => {
          return (
            <div className="flex w-full flex-row items-center justify-end gap-3">
              {original.type === 'test' ||
              (original.type === 'variant' &&
                original.variant.data().websiteIds.length <= 1) ? (
                <>
                  <Button
                    variant="primary"
                    className="mr-1"
                    size="small"
                    onClick={() => {
                      if (original.type === 'test') {
                        setOpenEditor(original.test.id);
                      } else if (original.tests[0].id) {
                        setOpenEditor(original.tests[0].id);
                      }
                    }}
                  >
                    Edit
                    <span className="sr-only">, Edit</span>
                  </Button>
                  <IconButton
                    variant="ternary"
                    onClick={() => {
                      window.open(
                        `https://${primaryDomain?.data().domain}/${
                          selectedDynamicPage?.data().path
                        }?utm=test_${original.type === 'variant' ? original.tests[0].id : original.test.id}`,
                        '_blank',
                      );
                    }}
                  >
                    <ArrowTopRightOnSquareIcon className="h-5 w-5" />
                    <span className="sr-only">, Visit</span>
                  </IconButton>
                </>
              ) : (
                <></>
              )}
              <MenuButton
                items={
                  original.type === 'variant'
                    ? [
                        {
                          title: 'Edit Name & Description',
                          onClick: () => {
                            if (original.type === 'variant') {
                              setEditNameAndDescriptionModalOpen({
                                variantId: original.variant.id,
                              });
                            }
                          },
                        },
                        {
                          title: 'Edit Smart Traffic Rules',
                          onClick: () => {
                            if (original.type === 'variant') {
                              setEditVariantConstraintsModelOpen({
                                variantId: original.variant.id,
                              });
                            }
                          },
                        },
                        {
                          title:
                            original.variant.data().status === 'active'
                              ? 'Deactivate Variant'
                              : 'Activate Variant',
                          onClick: () => {
                            if (!selectedAccount || !selectedDynamicPage)
                              return;

                            updateVariant(
                              selectedAccount.id,
                              selectedDynamicPage.id,
                              original.variant.id,
                              {
                                status:
                                  original.variant.data().status === 'active'
                                    ? 'disabled'
                                    : 'active',
                              },
                            );
                          },
                        },
                        {
                          title: 'Duplicate Variant',
                          onClick: () => {
                            if (!selectedAccount || !selectedDynamicPage)
                              return;

                            const sb = show('Duplicating variant...', 'info', {
                              disableAutoClose: true,
                            });

                            duplicateVariant(
                              selectedAccount.id,
                              selectedDynamicPage.id,
                              original.variant.id,
                            )
                              .then(() => {
                                show('Variant duplicated', 'info');
                              })
                              .catch((e) => {
                                console.error(e);
                                show('Failed to duplicate variant', 'warn');
                                Sentry.captureException(e);
                              })
                              .finally(() => {
                                hide(sb.id);
                              });
                          },
                        },
                        {
                          title: 'Duplicate Variant as New Dynamic Page',
                          onClick: () => {
                            if (!selectedAccount || !selectedDynamicPage)
                              return;

                            const sb = show(
                              'Duplicating variant as new page...',
                              'info',
                              {
                                disableAutoClose: true,
                              },
                            );

                            duplicateVariantAsNewDynamicPage(
                              selectedAccount.id,
                              selectedDynamicPage.id,
                              original.variant.id,
                            )
                              .then((res) => {
                                show('Variant duplicated', 'info');
                                navigate(
                                  `/workspace/${slug}/pages/${res.campaignId}/details`,
                                );
                              })
                              .catch((e) => {
                                console.error(e);
                                show('Failed to duplicate variant', 'warn');
                                Sentry.captureException(e);
                              })
                              .finally(() => {
                                hide(sb.id);
                              });
                          },
                        },
                        ...(original.tests.length > 1
                          ? [
                              {
                                title: 'Add New Experiment',
                                onClick: () => {
                                  if (
                                    !selectedAccount ||
                                    !selectedDynamicPage ||
                                    !original.tests[0]
                                  ) {
                                    console.log(
                                      'no account or dynamic page or test',
                                      {
                                        selectedAccount,
                                        selectedDynamicPage,
                                        tests: original.tests,
                                      },
                                    );
                                    show(
                                      'Failed to duplicate experiment',
                                      'warn',
                                    );
                                    return;
                                  }

                                  const sb = show(
                                    'Duplicating experiment...',
                                    'info',
                                    { disableAutoClose: true },
                                  );
                                  duplicateWebsiteInVariant(
                                    selectedAccount.id,
                                    selectedDynamicPage.id,
                                    original.variant.id,
                                    original.tests[0].id,
                                  )
                                    .then(() => {
                                      show('Experiment duplicated', 'info');
                                    })
                                    .catch((e) => {
                                      console.error(e);
                                      show(
                                        'Failed to duplicate experiment',
                                        'warn',
                                      );
                                      Sentry.captureException(e);
                                    })
                                    .finally(() => {
                                      hide(sb.id);
                                    });
                                },
                              },
                            ]
                          : [
                              {
                                title: 'View Analytics',
                                onClick: () => {
                                  setAnalyticsModalOpen({
                                    websiteId: original.tests[0].id,
                                  });
                                },
                              },
                            ]),
                        {
                          title: 'Delete Variant',
                          onClick: () => {
                            onDeleteVariantButtonClick(original.variant);
                          },
                          destructive: true,
                        },
                      ]
                    : [
                        // {
                        //   title: 'Edit Name & Description',
                        //   onClick: () => {
                        //     if (original.type === 'test') {
                        //       setEditNameAndDescriptionModalOpen({
                        //         variantId: original.variant.id,
                        //       });
                        //     }
                        //   },
                        // },
                        {
                          title: 'Edit Test Description',
                          onClick: () => {
                            setEditTestDescriptionModalOpen({
                              testId: original.test.id,
                            });
                          },
                        },
                        {
                          title: 'Mark as Winner (End Experiment)',
                          onClick: () => {
                            if (!selectedAccount || !selectedDynamicPage)
                              return;

                            const sb = show('Ending experiment...', 'info', {
                              disableAutoClose: true,
                            });
                            endExperiment(
                              selectedAccount.id,
                              selectedDynamicPage.id,
                              original.variant.id,
                              original.test.id,
                            )
                              .then(() => {
                                show('Experiment ended', 'info');
                              })
                              .catch((e) => {
                                console.error(e);
                                show('Failed to end experiment', 'warn');
                                Sentry.captureException(e);
                              })
                              .finally(() => {
                                hide(sb.id);
                              });
                          },
                        },
                        {
                          title: 'Edit Traffic Split',
                          onClick: () => {
                            setEditTrafficSplitModal({
                              variantId: original.variant.id,
                              tests: original.tests,
                            });
                          },
                        },
                        {
                          title: 'View Analytics',
                          onClick: () => {
                            setAnalyticsModalOpen({
                              websiteId: original.test.id,
                            });
                          },
                        },
                        {
                          title: 'Duplicate as another Experiment',
                          onClick: () => {
                            if (!selectedAccount || !selectedDynamicPage)
                              return;

                            const sb = show(
                              'Duplicating experiment as another experiment...',
                              'info',
                              {
                                disableAutoClose: true,
                              },
                            );

                            duplicateWebsiteInVariant(
                              selectedAccount.id,
                              selectedDynamicPage.id,
                              original.variant.id,
                              original.test.id,
                            )
                              .then(() => {
                                show(
                                  'Experiment duplicated as a new experiment',
                                  'info',
                                );
                              })
                              .catch((e) => {
                                console.error(e);
                                show('Failed to duplicate', 'warn');
                                Sentry.captureException(e);
                              })
                              .finally(() => {
                                hide(sb.id);
                              });
                          },
                        },
                        {
                          title: 'Duplicate as another Variant',
                          onClick: () => {
                            if (!selectedAccount || !selectedDynamicPage)
                              return;

                            const sb = show(
                              'Duplicating experiment as another variant...',
                              'info',
                              {
                                disableAutoClose: true,
                              },
                            );

                            duplicateWebsiteAsVariant(
                              selectedAccount.id,
                              selectedDynamicPage.id,
                              original.variant.id,
                              original.test.id,
                            )
                              .then(() => {
                                show(
                                  'Experiment duplicated as a new variant',
                                  'info',
                                );
                              })
                              .catch((e) => {
                                console.error(e);
                                show('Failed to duplicate', 'warn');
                                Sentry.captureException(e);
                              })
                              .finally(() => {
                                hide(sb.id);
                              });
                          },
                        },
                        ...(original.variant.data().websiteIds.length > 2
                          ? [
                              {
                                title: 'Remove Variation From Experiment',
                                onClick: () => {
                                  onDeleteTestButtonClick(
                                    original.test,
                                    original.variant,
                                  );
                                },
                                destructive: true,
                              },
                            ]
                          : []),
                      ]
                }
              />
            </div>
          );
        },
      },
    ] satisfies ColumnDef<DynamicPageVariantsRowData>[];
  }, [
    analyticsCustomEvents,
    analyticsExternalPixelEvents,
    analyticsType,
    getItemMetadataCached,
    hide,
    onDeleteTestButtonClick,
    onDeleteVariantButtonClick,
    openEditor,
    primaryDomain,
    selectedAccount,
    selectedDynamicPage,
    show,
  ]);

  if (!variants || !selectedDynamicPage) {
    return (
      <div className="flex w-full justify-center pt-24">
        <Loader />
      </div>
    );
  }

  if (
    selectedDynamicPage?.data().creationStatus === 'pending' &&
    !websites?.length
  ) {
    return (
      <div className="flex h-full items-center justify-center">
        <Lottie animationData={websiteBuildingAnimation} loop={true} />
      </div>
    );
  }

  if (variants.length === 0) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <NoVariantExists
          onCreateClick={(v) => setCreateVariantModalOpen({ approach: v.type })}
        />
        <CreateVariantModal
          defaultData={createVariantModalOpen}
          open={!!createVariantModalOpen}
          onClose={() => setCreateVariantModalOpen(undefined)}
        />
      </div>
    );
  }

  return (
    <>
      <EditorModal
        websiteId={openEditor || ''}
        open={!!openEditor}
        onClose={() => setOpenEditor(undefined)}
      />
      <CopyDynamicPageLinkModal
        open={copyLinkModalOpen}
        onClose={() => setCopyLinkModalOpen(false)}
      />
      <EditVariantNameAndDescription
        variantId={editNameAndDescriptionModalOpen?.variantId}
        open={!!editNameAndDescriptionModalOpen}
        onClose={() => setEditNameAndDescriptionModalOpen(undefined)}
      />
      <EditTestDescription
        testId={editTestDescriptionModalOpen?.testId}
        open={!!editTestDescriptionModalOpen}
        onClose={() => setEditTestDescriptionModalOpen(undefined)}
      />
      <EditTrafficSplitModal
        variantId={editTrafficSplitModal?.variantId}
        tests={editTrafficSplitModal?.tests}
        open={!!editTrafficSplitModal}
        onClose={() => setEditTrafficSplitModal(undefined)}
      />
      <EditVariantConstraintsModal
        variantId={editVariantConstraintsModelOpen?.variantId}
        open={!!editVariantConstraintsModelOpen}
        onClose={() => setEditVariantConstraintsModelOpen(undefined)}
      />
      <AlertModal
        type={'warn'}
        open={!!deleteConfirmationModalOpen}
        onClose={() => setDeleteConfirmationModalOpen(undefined)}
        title={`Are you sure you wish to delete ${deleteConfirmationModalOpen?.entity} "${deleteConfirmationModalOpen?.name}"?`}
        description="This action cannot be undone."
        actions={[
          {
            title: 'Cancel',
            type: 'secondary',
            onClick: () => setDeleteConfirmationModalOpen(undefined),
          },
          {
            title: 'Delete',
            type: 'primary',
            color: 'negative',
            onClick: async () => {
              const sb = show('Deleting...', 'info', {
                disableAutoClose: true,
              });
              await deleteConfirmationModalOpen
                ?.onConfirm()
                ?.then(() => {
                  show('Deleted', 'info');
                })
                ?.catch((e) => {
                  console.error(e);
                  show('Failed to delete', 'warn');
                  Sentry.captureException(e);
                })
                .finally(() => {
                  hide(sb.id);
                });

              setDeleteConfirmationModalOpen(undefined);
            },
          },
        ]}
      />
      <WebsitePreviewModal
        defaultShowAnalytics
        websiteId={analyticsModalOpen?.websiteId ?? ''}
        open={!!analyticsModalOpen}
        onClose={() => setAnalyticsModalOpen(undefined)}
      />
      <CreateVariantModal
        defaultData={createVariantModalOpen}
        open={!!createVariantModalOpen}
        onClose={() => setCreateVariantModalOpen(undefined)}
      />

      {!openEditor && (
        <>
          <div className="mt-8 flex w-full flex-col">
            <div className="flex flex-col items-stretch justify-between gap-3 border-b border-gray-300 pb-3 lg:flex-row lg:items-center">
              <h1 className="text-2xl font-semibold leading-6 text-gray-900">
                Overview
              </h1>
              <div className="flex flex-col items-stretch gap-3 lg:flex-row lg:items-center">
                <AnalyticsTypeButton
                  analyticsTypeOptions={[null, 'device']}
                  analyticsType={analyticsType}
                  onChange={(analyticsType: AnalyticsTypeOption | null) => {
                    setAnalyticsType(analyticsType);
                  }}
                />
                <DashboardAnalyticsRangeSelector size="small" variant="text" />
              </div>
            </div>
            <DynamicStats
              stats={[...stats, ...adAccountStats]}
              className="mt-6 overflow-x-auto"
            />

            {/* <StatsGrid stats={stats} /> */}

            {/* {adAccountStats && <StatsGrid stats={adAccountStats} />} */}

            <div className="inline-block min-w-full py-8 pb-6 pt-12 align-middle">
              <div className="flex items-center justify-between border-b border-gray-300 pb-3">
                <h1 className="text-2xl font-semibold leading-6 text-gray-900">
                  Variants
                </h1>
                <Button
                  variant="secondary"
                  icon={<PlusIcon className="h-4 w-4" />}
                  onClick={() => {
                    setCreateVariantModalOpen({ approach: 'emotional' });
                  }}
                >
                  New Variant
                </Button>
              </div>
              <CustomDataGrid
                logicalName="DynamicPageVariants"
                columns={columns}
                data={rows}
                virtualized={false}
                rowClassName={(row) =>
                  row.original.type === 'test'
                    ? 'bg-gray-50 hover:bg-gray-100'
                    : ''
                }
                cellClassName="py-7"
                initialState={{
                  columnPinning: {
                    right: ['actions'],
                  },
                  columnVisibility: {
                    itemId: false,
                    buttonClicks: false,
                    formSubmissions: false,
                    uniqueLinkClicks: false,
                  },
                }}
              />
              {/* {selectedDynamicPage?.data().creationStatus === 'pending' ? (
            <div className="flex w-full justify-center pt-12">
              <Loader />
            </div>
          ) : (
            <></>
          )} */}
            </div>
          </div>
        </>
      )}
    </>
  );
}
